import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { channelAPI, messageAPI, getChatWebSocketUrl, workspaceAPI, fileAPI, chatFileAPI } from '../lib/api';
import { cn, getInitials, getUploadErrorMessage } from '../lib/utils';
import { Layout } from '../components/Layout';
import ThreadView from '../components/discussion/ThreadView';
import MentionInput from '../components/discussion/MentionInput';
import SearchMessages from '../components/discussion/SearchMessages';
import FileAttachment from '../components/discussion/FileAttachment';
import LinkPreview from '../components/discussion/LinkPreview';
import {
    Hash,
    Lock,
    Plus,
    Settings,
    Search,
    Users,
    Send,
    Smile,
    Paperclip,
    Pin,
    MoreHorizontal,
    Edit2,
    Trash2,
    Reply,
    X,
    ChevronDown,
    MessageSquare,
    Image,
    FileText,
    Film,
    Check,
    Loader2,
    BellOff,
    Bell,
    CheckCheck,
    Bookmark,
    FolderKanban
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
    AlertDialogAction,
} from '../components/ui/alert-dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/ui/tooltip';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';

const DiscussionPage = () => {
    const { currentWorkspace, refreshStorage } = useWorkspace();
    const { user } = useAuth();
    const { markChannelRead, refresh: refreshNotifications } = useNotifications();
    const [searchParams, setSearchParams] = useSearchParams();
    const [channels, setChannels] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [isCreateChannelOpen, setIsCreateChannelOpen] = useState(false);
    const wsRef = useRef(null);
    const [typingUsers, setTypingUsers] = useState([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [editContent, setEditContent] = useState('');
    const [members, setMembers] = useState([]);
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeThread, setActiveThread] = useState(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [showPinnedMessages, setShowPinnedMessages] = useState(false);
    const [showChannelSettings, setShowChannelSettings] = useState(false);
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const [channelMembers, setChannelMembers] = useState([]);
    const [editChannelName, setEditChannelName] = useState('');
    const [editChannelDescription, setEditChannelDescription] = useState('');
    const [editChannelPrivacy, setEditChannelPrivacy] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [settingsMemberSearch, setSettingsMemberSearch] = useState('');
    const messagesEndRef = useRef(null);
    const messageInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef(null);

    // Load channels
    useEffect(() => {
        if (currentWorkspace) {
            loadChannels();
            loadMembers();
        } else {
            setLoading(false);
        }
    }, [currentWorkspace]);

    // Load messages when channel changes
    useEffect(() => {
        if (selectedChannel) {
            loadMessages();
            connectWebSocket();
        }
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [selectedChannel]);

    // Scroll to bottom on new messages
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Auto-select channel from URL param (from notification click)
    useEffect(() => {
        const channelId = searchParams.get('channel');
        if (!channelId || channels.length === 0) return;
        const target = channels.find(ch => ch.id === channelId);
        if (target && (!selectedChannel || selectedChannel.id !== channelId)) {
            handleSelectChannel(target);
            setSearchParams({}, { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channels, searchParams]);

    const loadChannels = async () => {
        try {
            const res = await channelAPI.getByWorkspace(currentWorkspace.id);
            setChannels(res.data.channels || []);
            
            // Select first channel if none selected — use handleSelectChannel so mark-as-read fires
            if (!selectedChannel && res.data.channels?.length > 0) {
                handleSelectChannel(res.data.channels[0]);
            }
        } catch (error) {
            console.error('Failed to load channels:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadMembers = async () => {
        try {
            const res = await workspaceAPI.getMembers(currentWorkspace.id);
            setMembers(res.data || []);
        } catch (error) {
            console.error('Failed to load members:', error);
        }
    };

    const loadMessages = async () => {
        if (!selectedChannel) return;
        try {
            const res = await messageAPI.getByChannel(selectedChannel.id, { limit: 50 });
            setMessages(res.data.messages || []);
        } catch (error) {
            console.error('Failed to load messages:', error);
        }
    };

    const connectWebSocket = () => {
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }

        const wsUrl = getChatWebSocketUrl(selectedChannel.id, user.id);
        const newWs = new WebSocket(wsUrl);

        newWs.onopen = () => {
            console.log('Chat WebSocket connected');
        };

        newWs.onmessage = (event) => {
            const data = JSON.parse(event.data);
            handleWebSocketMessage(data);
        };

        newWs.onclose = () => {
            console.log('Chat WebSocket disconnected');
        };

        newWs.onerror = (error) => {
            console.error('Chat WebSocket error:', error);
        };

        wsRef.current = newWs;
    };

    const handleWebSocketMessage = (data) => {
        switch (data.type) {
            case 'new_message':
                // Ignore thread replies — they belong in ThreadView, not the main message list
                if (data.message.parent_id) break;
                setMessages(prev => {
                    // Dedup: don't add if message already exists
                    if (prev.find(m => m.id === data.message.id)) return prev;
                    return [...prev, data.message];
                });
                break;
            case 'message_updated':
                setMessages(prev => prev.map(msg => 
                    msg.id === data.message.id ? data.message : msg
                ));
                break;
            case 'message_deleted':
                setMessages(prev => prev.filter(msg => msg.id !== data.message_id));
                break;
            case 'typing':
                if (data.is_typing) {
                    setTypingUsers(prev => {
                        if (!prev.find(u => u.user_id === data.verified_user_id)) {
                            return [...prev, { user_id: data.verified_user_id, user_name: data.user_name }];
                        }
                        return prev;
                    });
                } else {
                    setTypingUsers(prev => prev.filter(u => u.user_id !== data.verified_user_id));
                }
                // Auto-remove typing after 3 seconds
                setTimeout(() => {
                    setTypingUsers(prev => prev.filter(u => u.user_id !== data.verified_user_id));
                }, 3000);
                break;
            case 'reaction_updated':
                setMessages(prev => prev.map(msg => 
                    msg.id === data.message_id 
                        ? { ...msg, reactions: data.reactions }
                        : msg
                ));
                break;
            case 'user_joined':
                // Could show a toast or update online users
                break;
            case 'user_left':
                setTypingUsers(prev => prev.filter(u => u.user_id !== data.verified_user_id));
                break;
            default:
                break;
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const sendMessage = async () => {
        if ((!newMessage.trim() && pendingAttachments.length === 0) || !selectedChannel) return;

        setSendingMessage(true);
        try {
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                // Send via WebSocket for real-time
                wsRef.current.send(JSON.stringify({
                    type: 'message',
                    content: newMessage,
                    parent_id: replyingTo?.id || null,
                    mentions: extractMentions(newMessage),
                    attachments: pendingAttachments,
                }));
                setNewMessage('');
                setReplyingTo(null);
                setPendingAttachments([]);
            } else {
                // Fallback to HTTP
                await messageAPI.create(selectedChannel.id, {
                    content: newMessage,
                    channel_id: selectedChannel.id,
                    parent_id: replyingTo?.id || null,
                    mentions: extractMentions(newMessage),
                    attachments: pendingAttachments,
                });
                setNewMessage('');
                setReplyingTo(null);
                setPendingAttachments([]);
                loadMessages();
            }
        } catch (error) {
            toast.error('Gagal mengirim pesan');
        } finally {
            setSendingMessage(false);
        }
    };

    const extractMentions = (text) => {
        // Extract @username mentions and convert to user_ids
        const mentionRegex = /@([a-zA-Z0-9_]+)/g;
        const mentions = [];
        let match;
        
        while ((match = mentionRegex.exec(text)) !== null) {
            const username = match[1];
            // Find user by matching username (case insensitive)
            const user = members.find(m => {
                const memberName = (m.user_name || m.name || '').replace(/\s+/g, '_');
                return memberName.toLowerCase() === username.toLowerCase();
            });
            
            if (user) {
                const userId = user.user_id || user.id;
                if (!mentions.includes(userId)) {
                    mentions.push(userId);
                }
            }
        }
        
        return mentions;
    };

    const handleTyping = () => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
            
            // Clear existing timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            
            // Stop typing after 2 seconds of inactivity
            typingTimeoutRef.current = setTimeout(() => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
                }
            }, 2000);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        setUploadingFile(true);
        const uploaded = [];

        for (const file of files) {
            try {
                // Validate file size (50MB max)
                const maxSize = 50 * 1024 * 1024;
                if (file.size > maxSize) {
                    toast.error(`${file.name} terlalu besar. Maksimal 50MB`);
                    continue;
                }

                const response = await chatFileAPI.upload(file, selectedChannel.id);
                uploaded.push(response.data);
                toast.success(`${file.name} berhasil diupload`);
            } catch (error) {
                console.error('Upload error:', error);
                toast.error(getUploadErrorMessage(error));
            }
        }

        if (uploaded.length > 0) refreshStorage();
        setPendingAttachments(prev => [...prev, ...uploaded]);
        setUploadingFile(false);

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeAttachment = (attachmentId) => {
        setPendingAttachments(prev => prev.filter(a => a.id !== attachmentId));
    };

    const uploadImageFile = async (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
            toast.error('Gambar terlalu besar. Maksimal 50MB');
            return;
        }
        setUploadingFile(true);
        try {
            const response = await chatFileAPI.upload(file, selectedChannel.id);
            setPendingAttachments(prev => [...prev, response.data]);
            toast.success('Gambar berhasil ditambahkan');
            refreshStorage();
        } catch (error) {
            toast.error(getUploadErrorMessage(error));
        } finally {
            setUploadingFile(false);
        }
    };

    const handleImagePaste = async (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const imageItem = items.find(item => item.type.startsWith('image/'));
        if (!imageItem) return;
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (file) await uploadImageFile(file);
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleImageDrop = async (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
        for (const file of files) {
            await uploadImageFile(file);
        }
    };

    const handleEditMessage = async (messageId) => {
        if (!editContent.trim()) {
            toast.error('Pesan tidak boleh kosong');
            return;
        }

        try {
            const response = await messageAPI.editMessage(messageId, editContent);
            // Update local message list
            setMessages(prev => prev.map(m => m.id === messageId ? response.data : m));
            setEditingMessage(null);
            setEditContent('');
            toast.success('Pesan berhasil diupdate');
        } catch (error) {
            toast.error('Gagal update pesan');
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!window.confirm('Apakah Anda yakin ingin menghapus pesan ini?')) return;

        try {
            await messageAPI.deleteMessage(messageId);
            // Update local message list
            setMessages(prev => prev.map(m =>
                m.id === messageId
                    ? { ...m, content: '[Pesan dihapus]', is_deleted: true, attachments: [], link_previews: [] }
                    : m
            ));
            toast.success('Pesan berhasil dihapus');
        } catch (error) {
            toast.error('Gagal menghapus pesan');
        }
    };

    const handlePinMessage = async (messageId) => {
        try {
            const response = await messageAPI.togglePin(messageId);
            // Update local message list
            setMessages(prev => prev.map(m =>
                m.id === messageId
                    ? { ...m, is_pinned: response.data.is_pinned }
                    : m
            ));
            toast.success(response.data.message);
        } catch (error) {
            toast.error('Gagal pin pesan');
        }
    };

    const handleBookmarkMessage = async (messageId) => {
        try {
            const response = await messageAPI.toggleBookmark(messageId);
            toast.success(response.data.message);
        } catch (error) {
            toast.error('Gagal bookmark pesan');
        }
    };

    const loadPinnedMessages = async () => {
        if (!selectedChannel) return;
        try {
            const response = await messageAPI.getPinnedMessages(selectedChannel.id);
            setPinnedMessages(response.data.messages || []);
            setShowPinnedMessages(true);
        } catch (error) {
            toast.error('Gagal memuat pesan yang dipin');
        }
    };

    const loadChannelMembers = async () => {
        if (!selectedChannel) return;
        try {
            // Get channel members
            const response = await channelAPI.getMembers(selectedChannel.id);
            const membersList = response.data || [];
            
            // Get online status for each member
            const membersWithStatus = membersList.map(member => ({
                ...member,
                is_online: onlineUsers.includes(member.user_id)
            }));
            
            setChannelMembers(membersWithStatus);
            setShowMembersModal(true);
        } catch (error) {
            console.error('Error loading channel members:', error);
            toast.error('Gagal memuat daftar anggota');
        }
    };


    const isChannelAdmin = () => {
        if (!selectedChannel || !user) return false;
        
        // Check if user is channel creator
        if (selectedChannel.created_by === user.id) return true;
        
        // Check if user is workspace owner or admin
        const workspace = currentWorkspace;
        if (workspace) {
            // Get user role in workspace
            const userRole = user.role; // Assuming role is stored in user object
            return userRole === 'owner' || userRole === 'admin';
        }
        
        return false;
    };

    const openChannelSettings = () => {
        if (!selectedChannel) return;
        
        setEditChannelName(selectedChannel.name);
        setEditChannelDescription(selectedChannel.description || '');
        setEditChannelPrivacy(selectedChannel.is_private || false);
        setSelectedMembers(selectedChannel.member_ids || []);
        setShowChannelSettings(true);
    };

    const handleSaveChannelSettings = async () => {
        if (!selectedChannel) return;
        
        setSavingSettings(true);
        try {
            // Update channel details
            await channelAPI.update(selectedChannel.id, {
                name: editChannelName,
                description: editChannelDescription,
                is_private: editChannelPrivacy,
                member_ids: editChannelPrivacy ? selectedMembers : undefined
            });
            
            toast.success('Channel settings berhasil diupdate');
            setShowChannelSettings(false);
            loadChannels(); // Reload channels
        } catch (error) {
            console.error('Error saving channel settings:', error);
            toast.error('Gagal menyimpan settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleDeleteChannel = async () => {
        if (!selectedChannel) return;
        
        setDeleteConfirm({
            type: 'channel',
            id: selectedChannel.id,
            name: selectedChannel.name
        });
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        
        try {
            await channelAPI.delete(deleteConfirm.id);
            toast.success('Channel berhasil dihapus');
            setSelectedChannel(null);
            setShowChannelSettings(false);
            loadChannels();
        } catch (error) {
            console.error('Error deleting channel:', error);
            toast.error('Gagal menghapus channel');
        } finally {
            setDeleteConfirm(null);
        }
    };


    const addEmoji = (emoji) => {
        setNewMessage(prev => prev + emoji.native);
        setShowEmojiPicker(false);
        messageInputRef.current?.focus();
    };

    const handleReaction = async (messageId, emoji) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'reaction',
                message_id: messageId,
                emoji: emoji,
                action: 'add'
            }));
        } else {
            try {
                await messageAPI.addReaction(messageId, emoji);
                loadMessages();
            } catch (error) {
                toast.error('Gagal menambahkan reaksi');
            }
        }
    };

    const removeReaction = async (messageId, emoji) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
                type: 'reaction',
                message_id: messageId,
                emoji: emoji,
                action: 'remove'
            }));
        } else {
            try {
                await messageAPI.removeReaction(messageId, emoji);
                loadMessages();
            } catch (error) {
                toast.error('Gagal menghapus reaksi');
            }
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const res = await fileAPI.upload(file);
            const fileUrl = res.data.url || fileAPI.getUrl(res.data.path);
            
            // Determine message type based on file
            let messageType = 'file';
            if (file.type.startsWith('image/')) messageType = 'image';
            else if (file.type.startsWith('video/')) messageType = 'video';

            // Send as message with attachment
            if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({
                    type: 'message',
                    content: file.name,
                    message_type: messageType,
                    attachments: [{
                        id: res.data.id,
                        filename: file.name,
                        file_url: fileUrl,
                        file_type: file.type,
                        file_size: file.size
                    }]
                }));
            }
        } catch (error) {
            toast.error('Gagal mengupload file');
        }
    };

    const handleSelectChannel = async (channel) => {
        // Optimistically update local channel list and global total first
        setChannels(prev => prev.map(ch =>
            ch.id === channel.id ? {
                ...ch,
                unread_count: 0,
                unread_mention_count: 0,
                last_mention_preview: null
            } : ch
        ));
        markChannelRead(channel.id);
        setSelectedChannel(channel);

        // Always mark channel as read when selected (view = read)
        try {
            await channelAPI.markAsRead(channel.id);
        } catch (error) {
            console.error('Failed to mark channel as read:', error);
        } finally {
            // Refresh global notification totals regardless of API outcome
            refreshNotifications();
        }
    };

    const handleMuteChannel = async (channelId, isMuted) => {
        try {
            if (isMuted) {
                await channelAPI.unmute(channelId);
                toast.success('Notifikasi channel diaktifkan');
            } else {
                await channelAPI.mute(channelId);
                toast.success('Channel dibisukan');
            }
            // Update local state
            setChannels(prev => prev.map(ch => 
                ch.id === channelId ? { ...ch, is_muted: !isMuted } : ch
            ));
            if (selectedChannel?.id === channelId) {
                setSelectedChannel(prev => ({ ...prev, is_muted: !isMuted }));
            }
        } catch (error) {
            toast.error('Gagal mengubah status notifikasi');
        }
    };

    const filteredChannels = channels.filter(ch => 
        ch.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Split channels into workspace channels (no space) and space channels (with space_id)
    const workspaceChannels = filteredChannels.filter(ch => !ch.space_id);
    const spaceChannels = filteredChannels.filter(ch => ch.space_id);

    // Group space channels by space_name
    const spaceChannelGroups = spaceChannels.reduce((groups, ch) => {
        const spaceName = ch.space_name || 'Unknown Space';
        if (!groups[spaceName]) groups[spaceName] = [];
        groups[spaceName].push(ch);
        return groups;
    }, {});

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                        <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>Pilih workspace terlebih dahulu</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex h-full bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Channel Sidebar */}
            <div className="w-64 border-r border-gray-200 flex flex-col bg-gray-50">
                {/* Header */}
                <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-semibold text-gray-800">Discussion</h2>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setIsCreateChannelOpen(true)}
                        >
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            placeholder="Cari channel..."
                            className="pl-8 h-8 text-sm"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {/* Channel List */}
                <div className="flex-1 overflow-y-auto p-2">
                    {/* Workspace Channels Group */}
                    <div className="mb-4">
                        <div className="text-xs font-semibold text-gray-500 uppercase px-2 mb-2 flex items-center">
                            <ChevronDown className="w-3 h-3 mr-1" />
                            Channels
                        </div>
                        {workspaceChannels.map(channel => (
                            <button
                                key={channel.id}
                                onClick={() => handleSelectChannel(channel)}
                                className={cn(
                                    'w-full flex flex-col gap-1 px-2 py-1.5 rounded-md text-sm transition-colors group',
                                    selectedChannel?.id === channel.id
                                        ? 'bg-brand-primary text-white'
                                        : 'text-gray-700 hover:bg-gray-200'
                                )}
                            >
                                {/* Top row: Icon + Name + Badges */}
                                <div className="flex items-center gap-2 w-full">
                                    {channel.is_private ? (
                                        <Lock className="w-4 h-4 flex-shrink-0" />
                                    ) : (
                                        <Hash className="w-4 h-4 flex-shrink-0" />
                                    )}
                                    <span className={cn(
                                        "truncate flex-1 text-left",
                                        channel.is_muted && "opacity-50"
                                    )}>
                                        {channel.name}
                                    </span>
                                    {channel.is_muted && (
                                        <BellOff className={cn(
                                            "w-3 h-3 flex-shrink-0",
                                            selectedChannel?.id === channel.id ? "text-white/70" : "text-gray-400"
                                        )} />
                                    )}
                                    {/* Mention indicator - show @ if has unread mentions */}
                                    {channel.unread_mention_count > 0 && !channel.is_muted && (
                                        <span className="ml-auto bg-green-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5 font-medium flex-shrink-0">
                                            <span className="text-[10px]">@</span>
                                            {channel.unread_mention_count}
                                        </span>
                                    )}
                                    {/* Regular unread count - only show if no mentions */}
                                    {channel.unread_count > 0 && !channel.unread_mention_count && !channel.is_muted && (
                                        <span className="ml-auto bg-red-500 text-white text-xs px-1.5 rounded-full min-w-[20px] text-center flex-shrink-0">
                                            {channel.unread_count > 99 ? '99+' : channel.unread_count}
                                        </span>
                                    )}
                                </div>
                                
                                {/* Bottom row: Mention preview (only if has unread mentions) */}
                                {channel.unread_mention_count > 0 && channel.last_mention_preview && !channel.is_muted && (
                                    <div className="flex items-center gap-1.5 pl-6 w-full">
                                        <span className={cn(
                                            "text-xs truncate flex-1",
                                            selectedChannel?.id === channel.id 
                                                ? "text-white/80" 
                                                : "text-gray-500"
                                        )}>
                                            {channel.last_mention_preview}
                                        </span>
                                        <span className={cn(
                                            "text-xs flex-shrink-0",
                                            selectedChannel?.id === channel.id 
                                                ? "text-white/90" 
                                                : "text-green-600"
                                        )}>
                                            @
                                        </span>
                                    </div>
                                )}
                            </button>
                        ))}
                        {workspaceChannels.length === 0 && spaceChannels.length === 0 && (
                            <p className="text-sm text-gray-400 px-2">Tidak ada channel</p>
                        )}
                    </div>

                    {/* Space Channels Groups */}
                    {Object.keys(spaceChannelGroups).length > 0 && (
                        <div className="mb-4">
                            <div className="text-xs font-semibold text-gray-500 uppercase px-2 mb-2 flex items-center">
                                <FolderKanban className="w-3 h-3 mr-1" />
                                Space Channels
                            </div>
                            {Object.entries(spaceChannelGroups).map(([spaceName, spaceChs]) => (
                                <div key={spaceName} className="mb-2">
                                    <div className="text-xs font-medium text-gray-400 px-2 py-1 flex items-center gap-1">
                                        <FolderKanban className="w-3 h-3" />
                                        {spaceName}
                                    </div>
                                    {spaceChs.map(channel => (
                                        <button
                                            key={channel.id}
                                            onClick={() => handleSelectChannel(channel)}
                                            className={cn(
                                                'w-full flex flex-col gap-1 px-2 py-1.5 rounded-md text-sm transition-colors group',
                                                selectedChannel?.id === channel.id
                                                    ? 'bg-brand-primary text-white'
                                                    : 'text-gray-700 hover:bg-gray-200'
                                            )}
                                        >
                                                {/* Top row: Icon + Name + Badges */}
                                                <div className="flex items-center gap-2 w-full">
                                                    {channel.is_private ? (
                                                        <Lock className="w-4 h-4 flex-shrink-0" />
                                                    ) : (
                                                        <Hash className="w-4 h-4 flex-shrink-0" />
                                                    )}
                                                    <span className={cn(
                                                        "truncate flex-1 text-left",
                                                        channel.is_muted && "opacity-50"
                                                    )}>
                                                        {channel.name}
                                                    </span>
                                                    {channel.is_muted && (
                                                        <BellOff className={cn(
                                                            "w-3 h-3 flex-shrink-0",
                                                            selectedChannel?.id === channel.id ? "text-white/70" : "text-gray-400"
                                                        )} />
                                                    )}
                                                    {/* Mention indicator - show @ if has unread mentions */}
                                                    {channel.unread_mention_count > 0 && !channel.is_muted && (
                                                        <span className="ml-auto bg-green-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5 font-medium flex-shrink-0">
                                                            <span className="text-[10px]">@</span>
                                                            {channel.unread_mention_count}
                                                        </span>
                                                    )}
                                                    {/* Regular unread count - only show if no mentions */}
                                                    {channel.unread_count > 0 && !channel.unread_mention_count && !channel.is_muted && (
                                                        <span className="ml-auto bg-red-500 text-white text-xs px-1.5 rounded-full min-w-[20px] text-center flex-shrink-0">
                                                            {channel.unread_count > 99 ? '99+' : channel.unread_count}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {/* Bottom row: Mention preview (only if has unread mentions) */}
                                                {channel.unread_mention_count > 0 && channel.last_mention_preview && !channel.is_muted && (
                                                    <div className="flex items-center gap-1.5 pl-6 w-full">
                                                        <span className={cn(
                                                            "text-xs truncate flex-1",
                                                            selectedChannel?.id === channel.id 
                                                                ? "text-white/80" 
                                                                : "text-gray-500"
                                                        )}>
                                                            {channel.last_mention_preview}
                                                        </span>
                                                        <span className={cn(
                                                            "text-xs flex-shrink-0",
                                                            selectedChannel?.id === channel.id 
                                                                ? "text-white/90" 
                                                                : "text-green-600"
                                                        )}>
                                                            @
                                                        </span>
                                                    </div>
                                                )}
                                            </button>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col">
                {selectedChannel ? (
                    <>
                        {/* Channel Header */}
                        <div className="h-14 border-b border-gray-200 px-4 flex items-center justify-between bg-white">
                            <div className="flex items-center gap-2">
                                {selectedChannel.is_private ? (
                                    <Lock className="w-5 h-5 text-gray-500" />
                                ) : (
                                    <Hash className="w-5 h-5 text-gray-500" />
                                )}
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-gray-800">{selectedChannel.name}</h3>
                                        {selectedChannel.is_muted && (
                                            <span className="text-xs text-gray-400 flex items-center gap-1">
                                                <BellOff className="w-3 h-3" />
                                                Dibisukan
                                            </span>
                                        )}
                                    </div>
                                    {selectedChannel.topic && (
                                        <p className="text-xs text-gray-500 truncate max-w-md">{selectedChannel.topic}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => setIsSearchOpen(true)}
                                >
                                    <Search className="w-4 h-4" />
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={loadChannelMembers}
                                    title="Lihat anggota channel"
                                >
                                    <Users className="w-4 h-4 mr-1" />
                                    {selectedChannel.member_count || 0}
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleMuteChannel(selectedChannel.id, selectedChannel.is_muted)}
                                    title={selectedChannel.is_muted ? "Aktifkan notifikasi" : "Bisukan channel"}
                                >
                                    {selectedChannel.is_muted ? (
                                        <Bell className="w-4 h-4" />
                                    ) : (
                                        <BellOff className="w-4 h-4" />
                                    )}
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={loadPinnedMessages}
                                    title="Lihat pesan yang dipin"
                                >
                                    <Pin className="w-4 h-4" />
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={openChannelSettings}
                                    title="Pengaturan channel"
                                    disabled={!isChannelAdmin()}
                                >
                                    <Settings className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {messages.map((message, index) => (
                                <MessageItem
                                    key={message.id}
                                    message={message}
                                    currentUser={user}
                                    onReaction={handleReaction}
                                    onRemoveReaction={removeReaction}
                                    onPin={handlePinMessage}
                                    onBookmark={handleBookmarkMessage}
                                    onDelete={handleDeleteMessage}
                                    onEdit={handleEditMessage}
                                    onReply={() => setReplyingTo(message)}
                                    onOpenThread={(msg) => setActiveThread(msg)}
                                    isEditing={editingMessage?.id === message.id}
                                    setEditingMessage={setEditingMessage}
                                    editContent={editContent}
                                    setEditContent={setEditContent}
                                    showAvatar={index === 0 || messages[index - 1]?.user_id !== message.user_id}
                                    members={members}
                                />
                            ))}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Typing Indicator */}
                        {typingUsers.length > 0 && (
                            <div className="px-4 py-1 text-sm text-gray-500 flex items-center gap-2">
                                <span className="flex gap-1">
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </span>
                                {typingUsers.map(u => u.user_name).join(', ')} sedang mengetik...
                            </div>
                        )}

                        {/* Reply Preview */}
                        {replyingTo && (
                            <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm">
                                    <Reply className="w-4 h-4 text-gray-500" />
                                    <span className="text-gray-500">Membalas</span>
                                    <span className="font-medium">{replyingTo.user?.name}</span>
                                    <span className="text-gray-400 truncate max-w-xs">{replyingTo.content}</span>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => setReplyingTo(null)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        )}

                        {/* Pending Attachments Preview */}
                        {pendingAttachments.length > 0 && (
                            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                                <div className="flex flex-wrap gap-2">
                                    {pendingAttachments.map(file => (
                                        <div
                                            key={file.id}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm"
                                        >
                                            {file.file_type === 'image' ? (
                                                <Image className="w-4 h-4 text-gray-600" />
                                            ) : (
                                                <FileText className="w-4 h-4 text-gray-600" />
                                            )}
                                            <span className="text-gray-700 truncate max-w-[150px]">
                                                {file.filename}
                                            </span>
                                            <button
                                                onClick={() => removeAttachment(file.id)}
                                                className="text-gray-400 hover:text-gray-600"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Message Input */}
                        <div className="p-4 border-t border-gray-200 bg-white">
                            <div className="flex items-end gap-2">
                                <div className="flex-1 relative">
                                    <MentionInput
                                        ref={messageInputRef}
                                        value={newMessage}
                                        onChange={(e) => {
                                            setNewMessage(e.target.value);
                                            handleTyping();
                                        }}
                                        onKeyPress={handleKeyPress}
                                        onPaste={handleImagePaste}
                                        onDragOver={handleImageDragOver}
                                        onDrop={handleImageDrop}
                                        placeholder={`Kirim pesan ke #${selectedChannel.name}... (ketik @ untuk mention, paste/drop gambar)`}
                                        className="min-h-[44px] max-h-32 resize-none pr-20"
                                        rows={1}
                                        members={members}
                                        channels={channels}
                                    />
                                    <div className="absolute right-2 bottom-2 flex items-center gap-1">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileSelect}
                                            className="hidden"
                                            multiple
                                            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingFile}
                                        >
                                            {uploadingFile ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Paperclip className="w-4 h-4" />
                                            )}
                                        </Button>
                                        <div className="relative">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            >
                                                <Smile className="w-4 h-4" />
                                            </Button>
                                            {showEmojiPicker && (
                                                <div className="absolute bottom-10 right-0 z-50">
                                                    <Picker
                                                        data={data}
                                                        onEmojiSelect={addEmoji}
                                                        theme="light"
                                                        previewPosition="none"
                                                        skinTonePosition="none"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Button
                                    onClick={sendMessage}
                                    disabled={(!newMessage.trim() && pendingAttachments.length === 0) || sendingMessage}
                                    className="h-11"
                                >
                                    {sendingMessage ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>Pilih channel untuk memulai diskusi</p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => setIsCreateChannelOpen(true)}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Buat Channel Baru
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Thread View Panel */}
            {activeThread && selectedChannel && (
                <ThreadView
                    parentMessage={activeThread}
                    channelId={selectedChannel.id}
                    onClose={() => setActiveThread(null)}
                    ws={wsRef.current}
                />
            )}

            {/* Create Channel Modal */}
            <CreateChannelModal
                isOpen={isCreateChannelOpen}
                onClose={() => setIsCreateChannelOpen(false)}
                workspaceId={currentWorkspace?.id}
                onCreated={(channel) => {
                    setChannels(prev => [...prev, channel]);
                    setSelectedChannel(channel);
                    setIsCreateChannelOpen(false);
                }}
                members={members}
            />

            {/* Search Messages Modal */}
            <SearchMessages
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                onSelectMessage={(message, channel) => {
                    setSelectedChannel(channel);
                    // Could scroll to message or highlight it
                }}
            />

            {/* Channel Members Modal */}
            <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Anggota Channel - #{selectedChannel?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="max-h-96 overflow-y-auto">
                        {channelMembers.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">Tidak ada anggota</p>
                        ) : (
                            <div className="space-y-2">
                                {channelMembers.map((member) => (
                                    <div
                                        key={member.user_id}
                                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50"
                                    >
                                        <div className="relative">
                                            <div className="w-10 h-10 rounded-full bg-brand-primary flex items-center justify-center text-white font-medium">
                                                {getInitials(member.user_name || 'U')}
                                            </div>
                                            <div className={cn(
                                                "absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white",
                                                member.is_online ? "bg-green-500" : "bg-gray-400"
                                            )} />
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">
                                                {member.user_name}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {member.is_online ? (
                                                    <span className="text-green-600">● Online</span>
                                                ) : (
                                                    <span className="text-gray-400">○ Offline</span>
                                                )}
                                            </p>
                                        </div>
                                        
                                        {member.role && (
                                            <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                                                {member.role}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Pinned Messages Modal */}
            <Dialog open={showPinnedMessages} onOpenChange={setShowPinnedMessages}>
                <DialogContent className="max-w-2xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pin className="w-5 h-5 text-yellow-500" />
                            Pesan yang Dipin - #{selectedChannel?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto space-y-3">
                        {pinnedMessages.length === 0 ? (
                            <p className="text-center text-gray-500 py-8">Belum ada pesan yang dipin</p>
                        ) : (
                            pinnedMessages.map((message) => (
                                <div
                                    key={message.id}
                                    className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                                            {getInitials(message.user?.name || 'U')}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-2 mb-1">
                                                <span className="font-semibold text-gray-900">
                                                    {message.user?.name || 'Unknown'}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {new Date(message.created_at).toLocaleDateString('id-ID', {
                                                        day: 'numeric',
                                                        month: 'short',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </div>
                                            <p className="text-gray-800 whitespace-pre-wrap break-words">
                                                {message.content}
                                            </p>
                                            
                                            {message.attachments && message.attachments.length > 0 && (
                                                <div className="mt-2">
                                                    {message.attachments.map((att, idx) => (
                                                        <FileAttachment key={idx} attachment={att} showPreview={false} />
                                                    ))}
                                                </div>
                                            )}
                                            
                                            <div className="mt-2 flex items-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        setShowPinnedMessages(false);
                                                        const messageEl = document.getElementById(`message-${message.id}`);
                                                        if (messageEl) {
                                                            messageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                            messageEl.classList.add('bg-yellow-100');
                                                            setTimeout(() => messageEl.classList.remove('bg-yellow-100'), 2000);
                                                        }
                                                    }}
                                                >
                                                    Lihat di Chat
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handlePinMessage(message.id)}
                                                >
                                                    Hapus Pin
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Channel Settings Modal */}
            <Dialog open={showChannelSettings} onOpenChange={setShowChannelSettings}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Pengaturan Channel - #{selectedChannel?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Channel Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Nama Channel
                            </label>
                            <input
                                type="text"
                                value={editChannelName}
                                onChange={(e) => setEditChannelName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                placeholder="nama-channel"
                            />
                        </div>

                        {/* Channel Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Deskripsi
                            </label>
                            <textarea
                                value={editChannelDescription}
                                onChange={(e) => setEditChannelDescription(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                placeholder="Deskripsi channel..."
                                rows={3}
                            />
                        </div>

                        {/* Privacy Setting */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <p className="font-medium text-gray-900">Channel Private</p>
                                <p className="text-sm text-gray-500">
                                    Hanya anggota yang ditambahkan bisa mengakses
                                </p>
                            </div>
                            <button
                                onClick={() => setEditChannelPrivacy(!editChannelPrivacy)}
                                className={cn(
                                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                                    editChannelPrivacy ? "bg-brand-primary" : "bg-gray-300"
                                )}
                            >
                                <span
                                    className={cn(
                                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                                        editChannelPrivacy ? "translate-x-6" : "translate-x-1"
                                    )}
                                />
                            </button>
                        </div>

                        {/* Member Selection for Private Channel */}
                        {editChannelPrivacy && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Anggota Channel ({selectedMembers.length})
                                </label>
                                <div className="relative mb-2">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        value={settingsMemberSearch}
                                        onChange={(e) => setSettingsMemberSearch(e.target.value)}
                                        placeholder="Cari member..."
                                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                    />
                                </div>
                                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                                    {members
                                        .filter(m => {
                                            const q = settingsMemberSearch.toLowerCase();
                                            return !q || (m.user_name || '').toLowerCase().includes(q) || (m.user_email || '').toLowerCase().includes(q);
                                        })
                                        .map((member) => (
                                        <label
                                            key={member.user_id}
                                            className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedMembers.includes(member.user_id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedMembers([...selectedMembers, member.user_id]);
                                                    } else {
                                                        setSelectedMembers(selectedMembers.filter(id => id !== member.user_id));
                                                    }
                                                }}
                                                className="rounded"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <span className="text-sm text-gray-800">{member.user_name}</span>
                                                <span className="text-xs text-gray-400 ml-2">{member.user_email}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between pt-4 border-t">
                            <Button
                                variant="destructive"
                                onClick={handleDeleteChannel}
                                disabled={savingSettings}
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Hapus Channel
                            </Button>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowChannelSettings(false)}
                                    disabled={savingSettings}
                                >
                                    Batal
                                </Button>
                                <Button
                                    onClick={handleSaveChannelSettings}
                                    disabled={!editChannelName.trim() || savingSettings}
                                >
                                    {savingSettings ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Menyimpan...
                                        </>
                                    ) : (
                                        'Simpan'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus Channel?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Apakah Anda yakin ingin menghapus <strong>#{deleteConfirm?.name}</strong>? 
                            Semua pesan akan terhapus permanen. Tindakan ini tidak dapat dibatalkan.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteConfirm(null)}>
                            Batal
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete}>
                            Hapus
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
        </Layout>
    );
};

// Message Item Component
const MessageItem = ({
    message,
    currentUser,
    onReaction,
    onRemoveReaction,
    onPin,
    onBookmark,
    onDelete,
    onEdit,
    onReply,
    onOpenThread,
    isEditing,
    setEditingMessage,
    editContent,
    setEditContent,
    showAvatar,
    members
}) => {
    const [localEditContent, setLocalEditContent] = useState(message.content);
    const [showReactionPicker, setShowReactionPicker] = useState(false);
    const isOwn = message.user_id === currentUser?.id;

    const quickReactions = ['👍', '❤️', '😂', '🎉', '🔥', '👀'];

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const timeOpts = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Jakarta' };
        const timeStr = date.toLocaleTimeString('id-ID', timeOpts) + ' WIB';
        const dateInWIB = date.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' });
        const nowInWIB = now.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' });
        if (dateInWIB === nowInWIB) return timeStr;
        const dateDisplay = date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', timeZone: 'Asia/Jakarta' });
        return `${dateDisplay}, ${timeStr}`;
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        if (date.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' }) === today.toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' })) {
            return 'Hari ini';
        }
        return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', timeZone: 'Asia/Jakarta' });
    };

    // Group reactions by emoji
    const groupedReactions = (message.reactions || []).reduce((acc, r) => {
        if (!acc[r.emoji]) {
            acc[r.emoji] = { emoji: r.emoji, users: [], count: 0 };
        }
        acc[r.emoji].users.push(r.user_id);
        acc[r.emoji].count++;
        return acc;
    }, {});

    const hasReacted = (emoji) => {
        return groupedReactions[emoji]?.users.includes(currentUser?.id);
    };

    // Render content with file preview
    const renderContent = () => {
        return (
            <div>
                {/* Text content */}
                {message.is_deleted ? (
                    <p className="text-gray-400 italic">[Pesan dihapus]</p>
                ) : (
                    <p className="text-gray-800 whitespace-pre-wrap break-words">{message.content}</p>
                )}
                
                {/* File Attachments */}
                {message.attachments && message.attachments.length > 0 && (
                    <div className="space-y-2 mt-2">
                        {message.attachments.map((attachment, idx) => (
                            <FileAttachment key={idx} attachment={attachment} />
                        ))}
                    </div>
                )}
                
                {/* Link Previews */}
                {message.link_previews && message.link_previews.length > 0 && (
                    <div className="space-y-2 mt-2">
                        {message.link_previews.map((preview, idx) => (
                            <LinkPreview key={idx} preview={preview} />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (isEditing) {
        return (
            <div className="flex gap-3 px-4 py-2 bg-yellow-50 rounded-lg">
                <div className="flex-1">
                    <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="min-h-[60px]"
                    />
                    <div className="flex gap-2 mt-2">
                        <Button size="sm" onClick={() => onEdit(message.id)}>
                            <Check className="w-4 h-4 mr-1" /> Simpan
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingMessage(null)}>
                            Batal
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div 
            id={`message-${message.id}`}
            className={cn(
                'group flex gap-3 px-4 py-1 hover:bg-gray-50 rounded-lg transition-colors',
                message.is_pinned && 'bg-yellow-50'
            )}
        >
            {/* Avatar */}
            {showAvatar ? (
                <div className="w-9 h-9 rounded-full bg-brand-primary flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                    {getInitials(message.user?.name || 'U')}
                </div>
            ) : (
                <div className="w-9 flex-shrink-0" />
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
                {showAvatar && (
                    <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="font-semibold text-gray-900 text-sm">
                            {message.user?.name || 'Unknown'}
                        </span>
                        <span className="text-xs text-gray-400">
                            {formatTime(message.created_at)}
                        </span>
                        {message.is_edited && (
                            <span className="text-xs text-gray-400">(diedit)</span>
                        )}
                        {message.is_pinned && (
                            <Pin className="w-3 h-3 text-yellow-500" />
                        )}
                        {/* Read receipt - only show for own messages */}
                        {isOwn && (
                            <span className="flex items-center">
                                {message.read_by && message.read_by.length > 1 ? (
                                    <CheckCheck className="w-3.5 h-3.5 text-blue-500" title="Dibaca" />
                                ) : (
                                    <CheckCheck className="w-3.5 h-3.5 text-gray-400" title="Terkirim" />
                                )}
                            </span>
                        )}
                    </div>
                )}
                
                {renderContent()}

                {/* Reactions */}
                {Object.keys(groupedReactions).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {Object.values(groupedReactions).map((r) => (
                            <button
                                key={r.emoji}
                                onClick={() => hasReacted(r.emoji) 
                                    ? onRemoveReaction(message.id, r.emoji)
                                    : onReaction(message.id, r.emoji)
                                }
                                className={cn(
                                    'px-2 py-0.5 rounded-full text-sm flex items-center gap-1 transition-colors',
                                    hasReacted(r.emoji)
                                        ? 'bg-blue-100 border border-blue-300'
                                        : 'bg-gray-100 hover:bg-gray-200'
                                )}
                            >
                                <span>{r.emoji}</span>
                                <span className="text-xs">{r.count}</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Thread preview */}
                {message.reply_count > 0 && (
                    <button 
                        onClick={() => onOpenThread && onOpenThread(message)}
                        className="flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                    >
                        <MessageSquare className="w-4 h-4" />
                        {message.reply_count} balasan
                    </button>
                )}
            </div>

            {/* Actions */}
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-start gap-1">
                {/* Quick reactions */}
                <div className="flex bg-white shadow rounded-md border">
                    {quickReactions.slice(0, 4).map(emoji => (
                        <button
                            key={emoji}
                            onClick={() => onReaction(message.id, emoji)}
                            className="p-1 hover:bg-gray-100 text-sm"
                        >
                            {emoji}
                        </button>
                    ))}
                    <div className="relative">
                        <button
                            onClick={() => setShowReactionPicker(!showReactionPicker)}
                            className="p-1 hover:bg-gray-100"
                        >
                            <Smile className="w-4 h-4" />
                        </button>
                        {showReactionPicker && (
                            <div className="absolute top-8 right-0 z-50">
                                <Picker
                                    data={data}
                                    onEmojiSelect={(e) => {
                                        onReaction(message.id, e.native);
                                        setShowReactionPicker(false);
                                    }}
                                    theme="light"
                                    previewPosition="none"
                                    skinTonePosition="none"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* More actions */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={onReply}>
                            <Reply className="w-4 h-4 mr-2" />
                            Balas
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onPin(message.id, message.is_pinned)}>
                            <Pin className="w-4 h-4 mr-2" />
                            {message.is_pinned ? 'Hapus Pin' : 'Pin Pesan'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onBookmark(message.id)}>
                            <Bookmark className="w-4 h-4 mr-2" />
                            Bookmark
                        </DropdownMenuItem>
                        {isOwn && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => {
                                    setEditingMessage(message);
                                    setEditContent(message.content);
                                }}>
                                    <Edit2 className="w-4 h-4 mr-2" />
                                    Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                    onClick={() => onDelete(message.id)}
                                    className="text-red-600"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Hapus
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
};

// Create Channel Modal
const CreateChannelModal = ({ isOpen, onClose, workspaceId, onCreated, members }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [topic, setTopic] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [memberSearchQuery, setMemberSearchQuery] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error('Nama channel harus diisi');
            return;
        }

        setLoading(true);
        try {
            const res = await channelAPI.create(workspaceId, {
                name: name.toLowerCase().replace(/\s+/g, '-'),
                description,
                topic,
                is_private: isPrivate,
                workspace_id: workspaceId
            });

            // Add members if private
            if (isPrivate && selectedMembers.length > 0) {
                await channelAPI.addMembers(res.data.id, selectedMembers);
            }

            toast.success('Channel berhasil dibuat');
            onCreated(res.data);
            
            // Reset form
            setName('');
            setDescription('');
            setTopic('');
            setIsPrivate(false);
            setSelectedMembers([]);
            setMemberSearchQuery('');
        } catch (error) {
            toast.error('Gagal membuat channel');
        } finally {
            setLoading(false);
        }
    };

    // Filter members based on search query
    const filteredMembers = members.filter(member => {
        const memberName = (member.user_name || member.name || '').toLowerCase();
        const memberEmail = (member.user_email || member.email || '').toLowerCase();
        const query = memberSearchQuery.toLowerCase();
        return memberName.includes(query) || memberEmail.includes(query);
    });

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Buat Channel Baru</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label>Nama Channel</Label>
                        <div className="relative mt-1">
                            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="general"
                                className="pl-9"
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Huruf kecil, tanpa spasi</p>
                    </div>

                    <div>
                        <Label>Topic (opsional)</Label>
                        <Input
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="Topik singkat channel"
                            className="mt-1"
                        />
                    </div>

                    <div>
                        <Label>Deskripsi (opsional)</Label>
                        <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Deskripsi channel"
                            className="mt-1"
                            rows={2}
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="isPrivate"
                            checked={isPrivate}
                            onChange={(e) => setIsPrivate(e.target.checked)}
                            className="rounded"
                        />
                        <Label htmlFor="isPrivate" className="flex items-center gap-2 cursor-pointer">
                            <Lock className="w-4 h-4" />
                            Channel Private
                        </Label>
                    </div>

                    {isPrivate && (
                        <div>
                            <Label>Pilih Member</Label>
                            
                            {/* Search Input */}
                            <div className="relative mt-2">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <Input
                                    value={memberSearchQuery}
                                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                                    placeholder="Cari nama atau email member..."
                                    className="pl-9 pr-9"
                                />
                                {memberSearchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setMemberSearchQuery('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            
                            {/* Member List */}
                            <div className="mt-2 max-h-40 overflow-y-auto space-y-1 border rounded-md p-2">
                                {filteredMembers.map(member => (
                                    <label
                                        key={member.user_id}
                                        className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedMembers.includes(member.user_id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedMembers([...selectedMembers, member.user_id]);
                                                } else {
                                                    setSelectedMembers(selectedMembers.filter(id => id !== member.user_id));
                                                }
                                            }}
                                            className="rounded"
                                        />
                                        <span className="text-sm font-medium">{member.user_name || member.name}</span>
                                        <span className="text-xs text-gray-400">{member.user_email || member.email}</span>
                                    </label>
                                ))}
                                {filteredMembers.length === 0 && (
                                    <p className="text-sm text-gray-400 text-center py-2">
                                        {memberSearchQuery ? 'Tidak ada member yang sesuai' : 'Tidak ada member'}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Batal
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Buat Channel
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default DiscussionPage;
